import { ActionType, CompleteResourcesStepPayload } from "../../../../AppDataProvider/actions";
import { AppContext, BookingData } from "../../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, LoadingPill as BaseLoadingPill, List, ListItem } from "../../../../common/components";
import { CreateReservationMutationVariables, ResourceAllocationType, ResourceSelectionMode, useCreateReservationMutation } from "../../../../GeneratedGraphQL";
import { getReservationPartsInput, requireBookingDataFields } from "../../../../utils";
import { marginBottom, marginTop, media, typography } from "../../../../common/styles";
import React, { useContext, useState } from "react";
import { Resource, TimeSlot } from "../../../../AppDataProvider/types";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { PartRequired } from "../../../../utils/types";
import sortResourcesByGroup from "../../sortResourcesByGroup/sortResourcesByGroup";
import styled from "styled-components";
import { useDeleteExistingReservedSpace } from "../../../Availability/hooks/useDeleteExistingReservedSpace";
import { useTranslation } from "react-i18next";
export type GroupListProps = {
  resources: Resource[];
};
type ResourceStepBookingData = PartRequired<BookingData, "attendees" | "createdSource" | "duration" | "organisation" | "service" | "timezone">;
export function GroupList(props: GroupListProps) {
  const {
    resources
  } = props;
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  const {
    t
  } = useTranslation();
  const [createReservationLoading, setCreateReservationLoading] = useState<boolean>(false);
  const [deleteReservationLoading, setDeleteReservationLoading] = useState<boolean>(false);
  const [createReservation] = useCreateReservationMutation({
    errorPolicy: "all"
  });
  requireBookingDataFields<ResourceStepBookingData>(appData.bookingData, ["createdSource", "organisation", "service", "timezone"]);
  const {
    bookingTool
  } = appData;
  const {
    resourceSelectionMode
  } = bookingTool;
  const {
    bookingData
  } = appData;
  const {
    attendees,
    createdSource,
    duration,
    organisation,
    service,
    timezone,
    reservedSpaceId
  } = bookingData;
  const {
    resourceSelectionBeforeAvailability
  } = organisation;
  const {
    parts
  } = service;
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });
  type CreateStandardReservationOpts = {
    selectedResourceGroupId: string;
    selectedTimeSlot: TimeSlot;
  };
  const createStandardReservation = async ({
    selectedResourceGroupId,
    selectedTimeSlot
  }: CreateStandardReservationOpts) => {
    const {
      interval
    } = selectedTimeSlot;

    /**
     * Add 1 minute to reservation expiry on the back end.
     * This buffer means front-end will prompt for a reload 1 minute before
     * actual expiry, avoiding situations where user is interacting at same
     * time as reservation expiry.
     * Chronos only accepts full-minute values so 1 minute is easiest compromise
     */
    const {
      reservationExpiryMinutes
    } = organisation;
    const expiresAfter = reservationExpiryMinutes + 1;
    const variables: CreateReservationMutationVariables = {
      expiresAfter,
      parts: getReservationPartsInput({
        parts,
        selectedInterval: interval
      }),
      requiredSpaces: attendees,
      resourceGroupId: selectedResourceGroupId,
      serviceId: service._id,
      source: createdSource,
      timeZone: timezone
    };
    const {
      data,
      errors
    } = await createReservation({
      variables
    });
    if (errors?.some(error => error.message === "Response code 422 (Unprocessable Entity)")) {
      appDataDispatch({
        payload: {
          buttonLabel: t("reload"),
          content: t("errors.somethingChanged"),
          icon: faExclamationTriangle,
          onClick: /* istanbul ignore next */() => location.reload(),
          title: t("somethingChanged")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      return;
    } else if (!data || errors) {
      appDataDispatch({
        payload: {
          message: t("errors.cannotCreateReservation"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
      setCreateReservationLoading(false);
      return;
    }
    const {
      reservedSpace,
      reservation
    } = data.createReservation;
    setCreateReservationLoading(false);
    const updatedData = {
      attendees,
      duration,
      reservedSpaceId: reservedSpace._id,
      resourceGroupId: selectedResourceGroupId,
      startDate: new Date(reservation.start),
      timezone
    };
    const payload: CompleteResourcesStepPayload = {
      updatedData
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  };

  // Sort resources into groups and remove those which don't belong to any group
  // Casting the result to a more strict type, because we know for sure the
  // remaining groups must have an id and name, and we don't care about the
  // individual resources within the groups for this particular list view.
  const groups = sortResourcesByGroup(resources).filter(({
    groupId
  }) => groupId !== null) as {
    groupId: string;
    groupName: string;
  }[];
  const selectResourceGroup = (options: {
    groupId: string;
  }) => {
    /**
     * We have come to this step after availability and now
     * we need to create the reservation using the selected
     * resource.
     */
    if (resourceSelectionMode !== ResourceSelectionMode.Disabled && service.resourceAllocationType === ResourceAllocationType.Any && bookingData.selectedTimeSlot && !resourceSelectionBeforeAvailability) {
      const handleReservations = async (selectedTimeSlot: TimeSlot) => {
        if (reservedSpaceId) {
          setDeleteReservationLoading(true);
          await deleteExistingReservedSpace();
          setDeleteReservationLoading(false);
        }
        setCreateReservationLoading(true);
        void createStandardReservation({
          selectedResourceGroupId: options.groupId,
          selectedTimeSlot: selectedTimeSlot
        });
      };
      void handleReservations(bookingData.selectedTimeSlot);
      return;
    }
    const payload: CompleteResourcesStepPayload = {
      updatedData: {
        resourceGroupId: options.groupId
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  };
  return <List data-sentry-element="List" data-sentry-component="GroupList" data-sentry-source-file="GroupList.tsx">
      {(createReservationLoading || deleteReservationLoading) && <LoadingPill />}
      {groups.map(({
      groupId,
      groupName
    }) => <ListItem key={groupId}>
          <Name>{groupName}</Name>
          <Button onClick={() => {
        selectResourceGroup({
          groupId
        });
      }}>
            {t("select")}
          </Button>
        </ListItem>)}
    </List>;
}
const Name = styled.h2`
  ${typography.subtitle}
  ${marginTop.zero}
  @media ${media.mobile} {
    ${marginBottom.m}
  }
  /*
   * Was unset — renders {groupName} text directly inside
   * common/components' ListItem, which sets no ambient colour, so it
   * silently fell back to the browser default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Button = styled(BaseButton)`
  min-width: 6.25rem;
`;
const LoadingPill = styled(BaseLoadingPill)`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: absolute;
  justify-self: center;
  align-self: center;
`;
export default GroupList;